import React, { createContext, useContext, useState, ReactNode, useMemo } from "react";
import { fetchLocationsForDealers } from "../lib/api/dealers";

// Define the shape of the context value
type LocationContextType = {
  locations: { [dealerCode: string]: { latitude: number | null; longitude: number | null } };
  setLocations: React.Dispatch<React.SetStateAction<{ [dealerCode: string]: { latitude: number | null; longitude: number | null } }>>;
  getLocations: (dealers: string[]) => Promise<any>;
};

// Create the context with a default value
const LocationContext = createContext<LocationContextType | undefined>(undefined);

// Provider component
export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const [locations, setLocations] = useState<{ [dealerCode: string]: { latitude: number | null; longitude: number | null } }>({});

  const getLocations = async (dealers: string[]) => {
    try {
      const locations = await fetchLocationsForDealers(dealers);
      setLocations(locations);
    } catch (e) {
      return e;
    }
  };

  const value = useMemo(
    () => ({
      locations,
      setLocations,
      getLocations,
    }),
    [locations],
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

// Custom hook to use the LocationContext
export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};
